import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { showToast } from "vant";
import { Response } from "@/types/common";
import { useUserStore } from "@/stores/user";
import { i18n } from "@/i18n/index";
import { decryptBase64, decryptWithAes, encryptBase64, encryptWithAes, generateAesKey } from "./crypto";
import { decrypt, encrypt } from "./jsencrypt";

const { t } = i18n.global;
export const baseURL = process.env.VUE_APP_BASE_API;
export const tenantId = process.env.VUE_APP_BASE_TENANT_ID ?? "000000";
const clientId = process.env.VUE_APP_CLIENT_ID ?? "1";
const encryptHeader = "encrypt-key";

const _axios: AxiosInstance = axios.create({
  baseURL: baseURL + "/app",
  headers: {
    "Content-Type": "application/json",
    "clientid": clientId,
    'tenant-id' : tenantId
  },
  timeout: 50 * 1000,
});

_axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers["authorization"] = `Bearer ${token}`;
    config.headers["satoken"] = token;
  }

  const language = document.querySelector("html")?.getAttribute("language");
  if (language) {
    config.headers["language"] = language;
  }

  const lang = document.querySelector("html")?.getAttribute("lang");
  if (lang) {
    config.headers["lang"] = lang;
  }
// 🔐 Encrypt only if enabled
  if (process.env.VUE_APP_ENCRYPT === "true") {
    if (config.method === "post" || config.method === "put") {
      const aesKey = generateAesKey();
      config.headers[encryptHeader] = encrypt(encryptBase64(aesKey));
      if (typeof config.data === "object") {
        config.data = encryptWithAes(JSON.stringify(config.data), aesKey); //param encrypt by random generated key
      } else if (config.data) {
        config.data = encryptWithAes(config.data, aesKey);
      }
    }
  }
  return config;
});

_axios.interceptors.response.use((response: AxiosResponse) => {
  if (response.status === 200) {
    if (process.env.VUE_APP_ENCRYPT === "true") {
      const keyStr = response.headers[encryptHeader];
      if (keyStr) {
        const base64Str = decrypt(keyStr); 
        const aesKey = decryptBase64(base64Str.toString()); 
        const decryptedData = decryptWithAes(response.data, aesKey);
        response.data = JSON.parse(decryptedData);
      }
    }
    if (response.data.code == 200) {
      return Promise.resolve(response.data);
    } else if (response.data.code == 401) {
       const popupFn = (window as any).openLoginPopup;
      popupFn?.(1);
       location.replace("#/")
       localStorage.setItem('token', '')
          
      const userStore = useUserStore();
      userStore.resetUserData();
      return Promise.reject({ handled: true, msg: t(response.data.msg), code: 401 });
    } else {
      return Promise.reject(t(response.data.msg ) || "Erro do sistema");
    }
  } else {
    showToast("Erro do sistema");
    return Promise.reject(response);
  }
},
  (error) => {
    const errorMessage = error?.message || "";
    //Detect network or timeout related issues
    if (
      error.code === "ECONNABORTED" || // timeout
      errorMessage.includes("timeout") ||
      errorMessage.includes("Network Error") ||
      errorMessage.includes("ERR_CONNECTION") ||
      errorMessage.includes("ERR_NETWORK")
    ) {
      location.replace("#/notice")
    } else {
      showToast(t("System error, please try again later"));
    }

    return Promise.reject(error);
  }
);

export const get = (url: string, params?: any): Promise<Response> =>
  _axios.get(url, { params });

export const post = (url: string, params?: any): Promise<Response> =>
  _axios.post(url, params);

